/**
 * API utilities for presentation operations with direct Firebase integration
 * Handles video file uploads directly to Firebase Storage
 */
import { callFirebaseFunction } from '../firebaseCalls';
import { storage, db, auth } from '../../firebaseClient';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc, FieldValue } from 'firebase/firestore';

export interface PresentationResult {
  success: boolean;
  presentationId?: string;
  message?: string;
  error?: string;
  presentation?: any;
  presentations?: any[];
}

export interface UploadResult {
  success: boolean;
  presentationId: string;
  message: string;
  downloadURL: string;
  storagePath: string;
  fileName: string;
  fileSize: number;
  error?: string;
}

/**
 * Create a new presentation record directly in Firestore
 * @param {Object} presentationData - Presentation metadata
 * @param {string} videoUrl - Download URL of uploaded video
 * @param {string} videoPath - Storage path of uploaded video
 * @returns {Promise<PresentationResult>} Created presentation result
 */
export const createPresentationRecord = async (presentationData: any, videoUrl: string, videoPath: string): Promise<PresentationResult> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get user's custom claims for tenant info
    const idTokenResult = await user.getIdTokenResult();
    const tenantId = idTokenResult.claims.tenantId;
    const isGlobalAdmin = idTokenResult.claims.isGlobalAdmin;

    if (!tenantId && !isGlobalAdmin) {
      throw new Error('No tenant ID found for user');
    }

    // Determine the consultant ID - use consultantId if provided, otherwise use current user ID
    const consultantId = presentationData.consultantId || user.uid;

    // CRITICAL: Get evaluation schema ID - either from presentationData or user's default
    // Check explicitly for null, undefined, or empty string
    let evaluationSchemaId = presentationData.evaluationSchemaId;

    // Normalize: treat empty string as null
    if (evaluationSchemaId === '' || evaluationSchemaId === null || evaluationSchemaId === undefined) {
      evaluationSchemaId = null;
    }

    console.log('🔍 DEBUG: Evaluation Schema ID resolution:', {
      providedEvaluationSchemaId: presentationData.evaluationSchemaId,
      evaluationSchemaIdType: typeof presentationData.evaluationSchemaId,
      evaluationSchemaIdValue: presentationData.evaluationSchemaId,
      normalizedEvaluationSchemaId: evaluationSchemaId,
      presentationDataKeys: Object.keys(presentationData)
    });

    if (!evaluationSchemaId) {
      // Check if user has default evaluation schema assigned
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        evaluationSchemaId = userData.defaultEvaluationSchemaId;
        console.log(`✅ Using user's default evaluation schema: ${evaluationSchemaId}`);
      }

      if (!evaluationSchemaId) {
        throw new Error('No evaluation criteria available. Either select a custom evaluation framework or contact your administrator to assign a default evaluation schema to your account.');
      }
    } else {
      console.log(`✅ Using provided evaluation schema ID: ${evaluationSchemaId}`);
    }

    console.log('🚀 DEBUG: Creating presentation record:', {
      providedConsultantId: presentationData.consultantId,
      resolvedConsultantId: consultantId,
      userId: user.uid,
      isCoachMode: presentationData.consultantId && presentationData.consultantId !== user.uid,
      finalEvaluationSchemaId: evaluationSchemaId,
      presentationData: presentationData
    });

    const presentationRecord = {
      ...presentationData,
      evaluationSchemaId, // CRITICAL: Explicitly set the evaluation schema ID (overrides any from presentationData)
      videoUrl,
      videoPath,
      uploadedBy: user.uid,
      userId: user.uid, // Required by Firestore rules - matches backend (always the uploader)
      consultantId, // The consultant being evaluated (can be different from uploader in coach mode)
      tenantId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'uploaded', // Upload status
      evaluationStatus: 'NEW' // Evaluation pipeline status - ready to start
    };

    console.log(`📝 Creating presentation with evaluationSchemaId: ${evaluationSchemaId}`);
    console.log(`📝 Presentation record evaluationSchemaId field: ${presentationRecord.evaluationSchemaId}`);
    console.log(`📝 Full presentation record (excluding large fields):`, {
      ...presentationRecord,
      videoUrl: presentationRecord.videoUrl ? '[URL present]' : '[no URL]',
      videoPath: presentationRecord.videoPath ? '[path present]' : '[no path]'
    });

    const docRef = await addDoc(collection(db, 'presentations'), presentationRecord);

    return {
      success: true,
      presentationId: docRef.id,
      message: 'Presentation created successfully'
    };
  } catch (error) {
    console.error('Error creating presentation record:', error);
    throw error;
  }
};

/**
 * Get a single presentation by ID
 * @param {string} presentationId - ID of the presentation to retrieve
 * @returns {Promise<PresentationResult>} Presentation details
 */
export const getPresentation = async (presentationId: string): Promise<PresentationResult> => {
  try {
    if (!presentationId) {
      throw new Error('Presentation ID is required');
    }

    // Try Firebase function first, fallback to direct Firestore access
    try {
      const result = await callFirebaseFunction('getPresentation', {
        presentationId
      }) as { success: boolean; presentation?: any; error?: string };

      // Debug logging for Firebase function result
      if (result.success && result.presentation) {
        console.log('DEBUG getPresentation Firebase function:', {
          presentationId: result.presentation.id,
          evaluationStatus: result.presentation.evaluationStatus,
          allData: result.presentation
        });
      }

      return result;
    } catch (functionError: any) {
      console.warn('Firebase function failed, using direct Firestore access:', functionError?.message || String(functionError));

      // Fallback to direct Firestore access
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idTokenResult = await user.getIdTokenResult();
      const tenantId = idTokenResult.claims.tenantId;

      if (!tenantId) {
        throw new Error('No tenant ID found for user');
      }

      // Get the document directly from Firestore
      const docRef = doc(db, 'presentations', presentationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return {
          success: false,
          error: 'Presentation not found'
        };
      }

      const presentation = docSnap.data();

      // Check tenant isolation
      if (presentation.tenantId !== tenantId && idTokenResult.claims.role !== 'globalAdmin') {
        return {
          success: false,
          error: 'Access denied: presentation belongs to different tenant'
        };
      }

      // Ensure evaluationStatus is set for existing presentations
      const presentationData = {
        id: docSnap.id,
        ...presentation,
        // Set evaluationStatus to 'NEW' if it doesn't exist
        evaluationStatus: presentation.evaluationStatus || 'NEW'
      };

      console.log('DEBUG getPresentation fallback:', {
        presentationId: docSnap.id,
        evaluationStatus: presentationData.evaluationStatus,
        originalStatus: presentation.evaluationStatus,
        allData: presentationData
      });

      return {
        success: true,
        presentation: presentationData
      };
    }
  } catch (error) {
    console.error('Error getting presentation:', error);
    throw error;
  }
};

/**
 * Fetch and aggregate evaluation scores for presentations
 * @param {Array} presentations - Array of presentation objects
 * @returns {Promise<Array>} Presentations with aggregated scores
 */
const fetchPresentationScores = async (presentations: any[]): Promise<any[]> => {
  try {
    // Create a map of presentations with updated scores
    const presentationMap = new Map();
    presentations.forEach((p: any) => presentationMap.set(p.id, { ...p }));

    presentations.forEach((presentation: any) => {
      // PRIORITY 1: Check if flat score fields already exist (our new fix)
      // These are the fields our backend fix now populates directly
      if (presentation.overallScore || presentation.structureScore || presentation.softSkillsScore || presentation.knowledgeScore) {
        // Flat scores exist - use them directly (no extraction needed)
        console.log(`Using flat scores for presentation ${presentation.id}:`, {
          overall: presentation.overallScore,
          structure: presentation.structureScore,
          softSkills: presentation.softSkillsScore,
          knowledge: presentation.knowledgeScore
        });
        return; // Skip other extraction methods
      }

      // PRIORITY 2: Check finalReport directly (for newly completed presentations)
      if (presentation.finalReport?.overall_score) {
        const categoryScores = presentation.finalReport.category_scores || {};

        presentation.overallScore = presentation.finalReport.overall_score;
        presentation.structureScore = categoryScores.structure;
        presentation.softSkillsScore = categoryScores.soft_skills;
        presentation.knowledgeScore = categoryScores.knowledge;

        console.log(`Extracted scores from finalReport for presentation ${presentation.id}`);
        return; // Skip other extraction methods
      }

      // PRIORITY 3: Legacy - check if scores are stored in stageResults (backwards compatibility)
      if (presentation.stageResults?.finalReport?.category_scores) {
        const categoryScores = presentation.stageResults.finalReport.category_scores;

        // Extract individual scores from the nested structure
        presentation.structureScore = categoryScores.structure;
        presentation.softSkillsScore = categoryScores.soft_skills;
        presentation.knowledgeScore = categoryScores.knowledge;

        // Extract overall score from the nested structure
        if (presentation.stageResults.finalReport.overall_score) {
          presentation.overallScore = presentation.stageResults.finalReport.overall_score;
        }

        console.log(`Extracted scores from stageResults for presentation ${presentation.id}`);
      }
    });

    // For presentations that still don't have scores, try to fetch from evaluationResults collection
    const presentationsNeedingScores = Array.from(presentationMap.values()).filter(p =>
      (p.evaluationStatus === 'COMPLETE' || p.status === 'COMPLETE') &&
      (!p.overallScore && !p.structureScore && !p.softSkillsScore && !p.knowledgeScore)
    );

    if (presentationsNeedingScores.length > 0) {
      console.log(`Fetching scores from evaluationResults for ${presentationsNeedingScores.length} presentations`);

      // Get evaluation results for these presentations
      const presentationIds = presentationsNeedingScores.map(p => p.id);

      // Query evaluationResults collection for category-level results
      const evaluationQuery = query(
        collection(db, 'evaluationResults'),
        where('presentationId', 'in', presentationIds)
      );

      const evaluationSnapshot = await getDocs(evaluationQuery);
      const evaluationsByPresentation: { [key: string]: { [category: string]: number } } = {};

      // Group evaluation results by presentation and category
      evaluationSnapshot.forEach((doc) => {
        const data = doc.data();
        const presentationId = data.presentationId;
        const category = data.category;

        if (!evaluationsByPresentation[presentationId]) {
          evaluationsByPresentation[presentationId] = {};
        }

        // Store the category score (overallScore from the evaluation result)
        evaluationsByPresentation[presentationId][category] = data.overallScore || 0;
      });

      // Update presentations with scores from evaluationResults
      Object.keys(evaluationsByPresentation).forEach(presentationId => {
        const presentation = presentationMap.get(presentationId);
        if (presentation) {
          const categoryScores = evaluationsByPresentation[presentationId];

          // Map category names to score fields (more comprehensive mapping)
          presentation.structureScore = categoryScores['Structure'] ||
            categoryScores['Presentation Structure'] ||
            categoryScores['structure'] || undefined;
          presentation.softSkillsScore = categoryScores['Soft Skills'] ||
            categoryScores['Communication Skills'] ||
            categoryScores['Presentation Skills'] ||
            categoryScores['soft_skills'] || undefined;
          presentation.knowledgeScore = categoryScores['Knowledge'] ||
            categoryScores['Technical Knowledge'] ||
            categoryScores['Subject Knowledge'] ||
            categoryScores['Product Knowledge'] ||
            categoryScores['knowledge'] || undefined;

          // Calculate overall score as average of available category scores
          const availableScores = [
            presentation.structureScore,
            presentation.softSkillsScore,
            presentation.knowledgeScore
          ].filter(score => typeof score === 'number' && !isNaN(score));

          if (availableScores.length > 0) {
            presentation.overallScore = availableScores.reduce((sum, score) => sum + score, 0) / availableScores.length;
          }
        }
      });
    }

    return Array.from(presentationMap.values());
  } catch (error) {
    console.error('Error fetching presentation scores:', error);
    // Return original presentations if score fetching fails
    return presentations;
  }
};

/**
 * Get presentations for the current user/tenant
 * @param {Object} filters - Optional filters
 * @returns {Promise<PresentationResult>} List of presentations
 */
export const getPresentations = async (filters: Record<string, any> = {}): Promise<PresentationResult> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get user's custom claims for tenant info
    const idTokenResult = await user.getIdTokenResult();
    const tenantId = idTokenResult.claims.tenantId;
    const isGlobalAdmin = idTokenResult.claims.role === 'globalAdmin';
    const isTenantAdmin = idTokenResult.claims.role === 'tenantAdmin';

    if (!tenantId && !isGlobalAdmin) {
      throw new Error('No tenant ID found for user');
    }

    let q;
    if (isGlobalAdmin) {
      // Global admins can see all presentations
      q = query(collection(db, 'presentations'));
    } else if (isTenantAdmin) {
      // Tenant admins can see all presentations in their tenant
      q = query(
        collection(db, 'presentations'),
        where('tenantId', '==', tenantId)
      );
    } else {
      // Regular users can only see their own presentations
      q = query(
        collection(db, 'presentations'),
        where('tenantId', '==', tenantId),
        where('userId', '==', user.uid)
      );
    }

    const querySnapshot = await getDocs(q);
    const presentations: any[] = [];

    querySnapshot.forEach((doc) => {
      presentations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Fetch scores from evaluationResults for completed presentations
    const presentationsWithScores = await fetchPresentationScores(presentations);

    return {
      success: true,
      presentations: presentationsWithScores
    };
  } catch (error) {
    console.error('Error getting presentations:', error);
    throw error;
  }
};

/**
 * Get presentations for dashboard KPIs based on user role
 * @param {Object} userClaims - User's custom claims (tenantId, isTenantAdmin, etc.)
 * @param {string} userId - Current user's ID
 * @returns {Promise<PresentationResult>} Presentations filtered by user visibility
 */
export const getPresentationsForDashboard = async (userClaims: any, userId: string): Promise<PresentationResult> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'User not authenticated',
        presentations: []
      };
    }

    const tenantId = userClaims?.tenantId;
    const isGlobalAdmin = userClaims?.isGlobalAdmin;

    if (!tenantId && !isGlobalAdmin) {
      return {
        success: false,
        error: 'No tenant ID found for user',
        presentations: []
      };
    }

    let q;

    // Check user role based on claims  
    const isTenantAdmin = userClaims.role === 'tenantAdmin';

    if (isGlobalAdmin) {
      // Global Admin: Get all presentations
      q = query(collection(db, 'presentations'));
    } else if (isTenantAdmin) {
      // Tenant Admin: Get all presentations in the tenant
      q = query(
        collection(db, 'presentations'),
        where('tenantId', '==', tenantId)
      );
    } else {
      // Regular User: Get only their own presentations
      q = query(
        collection(db, 'presentations'),
        where('tenantId', '==', tenantId),
        where('userId', '==', userId)
      );
    }

    const querySnapshot = await getDocs(q);
    const presentations: any[] = [];

    querySnapshot.forEach((doc) => {
      presentations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Fetch scores from evaluationResults for completed presentations
    const presentationsWithScores = await fetchPresentationScores(presentations);

    return {
      success: true,
      presentations: presentationsWithScores
    };
  } catch (error) {
    console.error('Error getting presentations for dashboard:', error);
    return {
      success: false,
      error: (error as any).message || 'Failed to load presentations',
      presentations: []
    };
  }
};

/**
 * ChunkedUploader class handles large file uploads
 */
class ChunkedUploader {
  file: File;
  presentationId: string;
  onProgress: ((progress: number) => void) | null;
  onComplete: ((result: any) => void) | null;
  onError: ((error: any) => void) | null;
  uploadSession: any;
  chunkSize: number;
  totalChunks: number;
  uploadedChunks: number;
  isUploading: boolean;
  isPaused: boolean;

  constructor(file: File, presentationId: string, onProgress: ((progress: number) => void) | null = null, onComplete: ((result: any) => void) | null = null, onError: ((error: any) => void) | null = null) {
    this.file = file;
    this.presentationId = presentationId;
    this.onProgress = onProgress;
    this.onComplete = onComplete;
    this.onError = onError;

    this.uploadSession = null;
    this.chunkSize = 5 * 1024 * 1024; // 5MB default
    this.totalChunks = 0;
    this.uploadedChunks = 0;
    this.isUploading = false;
    this.isPaused = false;
  }

  /**
   * Start the chunked upload process
   */
  async start() {
    try {
      this.isUploading = true;

      // Step 1: Initialize upload session
      const sessionResponse = await callFirebaseFunction('initiateChunkedUpload', {
        presentationId: this.presentationId,
        fileName: this.file.name,
        fileSize: this.file.size,
        contentType: this.file.type
      }) as { success: boolean; error?: string; chunkSize?: number; totalChunks?: number; uploadSessionId?: string };

      if (!sessionResponse.success) {
        throw new Error(sessionResponse.error || 'Failed to initiate upload');
      }

      this.uploadSession = sessionResponse;
      this.chunkSize = sessionResponse.chunkSize || this.chunkSize;
      this.totalChunks = sessionResponse.totalChunks || 0;

      console.log(`Starting chunked upload: ${this.totalChunks} chunks of ${this.chunkSize} bytes each`);

      // Step 2: Upload chunks sequentially
      for (let chunkNumber = 0; chunkNumber < this.totalChunks; chunkNumber++) {
        if (!this.isUploading || this.isPaused) {
          break;
        }

        await this.uploadChunk(chunkNumber);
        this.uploadedChunks++;

        // Update progress
        const progress = Math.round((this.uploadedChunks / this.totalChunks) * 100);
        if (this.onProgress) {
          this.onProgress(progress);
        }
      }

      // Step 3: Finalize upload if all chunks uploaded
      if (this.uploadedChunks === this.totalChunks && this.isUploading) {
        await this.finalizeUpload();
      }

    } catch (error) {
      console.error('Chunked upload error:', error);
      this.isUploading = false;
      if (this.onError) {
        this.onError(error);
      }
      throw error;
    }
  }

  /**
   * Upload a single chunk
   */
  async uploadChunk(chunkNumber: number) {
    try {
      // Calculate chunk boundaries
      const start = chunkNumber * this.chunkSize;
      const end = Math.min(start + this.chunkSize, this.file.size);
      const chunkData = this.file.slice(start, end);

      // Get upload URL for this chunk
      const urlResponse = await callFirebaseFunction('getChunkUploadUrl', {
        uploadSessionId: this.uploadSession.uploadSessionId,
        chunkNumber: chunkNumber
      }) as { success: boolean; error?: string; uploadUrl?: string };

      if (!urlResponse.success || !urlResponse.uploadUrl) {
        throw new Error(`Failed to get upload URL for chunk ${chunkNumber}: ${urlResponse.error || 'Unknown error'}`);
      }

      // Upload chunk using fetch
      const uploadResponse = await fetch(urlResponse.uploadUrl, {
        method: 'PUT',
        body: chunkData,
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload chunk ${chunkNumber}: ${uploadResponse.statusText}`);
      }

      // Confirm chunk upload
      const confirmResponse = await callFirebaseFunction('confirmChunkUploaded', {
        uploadSessionId: this.uploadSession.uploadSessionId,
        chunkNumber: chunkNumber
      }) as { success: boolean; error?: string };

      if (!confirmResponse.success) {
        throw new Error(`Failed to confirm chunk ${chunkNumber}: ${confirmResponse.error || 'Unknown error'}`);
      }

      console.log(`Chunk ${chunkNumber + 1}/${this.totalChunks} uploaded successfully`);

    } catch (error) {
      console.error(`Error uploading chunk ${chunkNumber}:`, error);
      throw error;
    }
  }

  /**
   * Finalize the upload by combining chunks
   */
  async finalizeUpload() {
    try {
      console.log('Finalizing chunked upload...');

      const finalizeResponse = await callFirebaseFunction('finalizeChunkedUpload', {
        uploadSessionId: this.uploadSession.uploadSessionId
      }) as { success: boolean; error?: string;[key: string]: any };

      if (!finalizeResponse.success) {
        throw new Error(`Failed to finalize upload: ${finalizeResponse.error || 'Unknown error'}`);
      }

      console.log('Upload finalized successfully');
      this.isUploading = false;

      if (this.onComplete) {
        this.onComplete(finalizeResponse);
      }

      return finalizeResponse;

    } catch (error) {
      console.error('Error finalizing upload:', error);
      throw error;
    }
  }

  /**
   * Pause the upload
   */
  pause() {
    this.isPaused = true;
    console.log('Upload paused');
  }

  /**
   * Resume the upload
   */
  resume() {
    this.isPaused = false;
    console.log('Upload resumed');
  }

  /**
   * Cancel the upload
   */
  cancel() {
    this.isUploading = false;
    this.isPaused = false;
    console.log('Upload cancelled');
  }

  /**
   * Get upload progress
   */
  getProgress() {
    if (this.totalChunks === 0) return 0;
    return Math.round((this.uploadedChunks / this.totalChunks) * 100);
  }
}

/**
 * Upload a presentation with video file directly to Firebase Storage
 * @param {Object} presentationData - Presentation metadata
 * @param {File} videoFile - Video file to upload
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<UploadResult>} Upload result
 */
export const uploadPresentation = async (presentationData: any, videoFile: File, onProgress: ((progress: number) => void) | null = null): Promise<UploadResult> => {
  try {
    console.log('Starting presentation upload...', {
      fileName: videoFile.name,
      fileSize: videoFile.size,
      presentationData
    });

    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get user's tenant ID
    const idTokenResult = await user.getIdTokenResult();
    const tenantId = idTokenResult.claims.tenantId;
    const isGlobalAdmin = idTokenResult.claims.isGlobalAdmin;

    if (!tenantId && !isGlobalAdmin) {
      throw new Error('No tenant ID found for user');
    }

    // Create storage reference with tenant isolation
    const timestamp = Date.now();
    const fileName = `${timestamp}_${videoFile.name}`;
    const storagePath = `presentations/${tenantId}/${user.uid}/${fileName}`;
    const storageRef = ref(storage, storagePath);

    console.log('Uploading to storage path:', storagePath);

    // Create upload task with progress tracking
    const uploadTask = uploadBytesResumable(storageRef, videoFile);

    return new Promise<UploadResult>((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          // Progress tracking
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload progress:', Math.round(progress) + '%');

          if (onProgress) {
            onProgress(Math.round(progress));
          }
        },
        (error) => {
          // Handle upload error
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            // Upload completed successfully, get download URL
            console.log('Upload completed, getting download URL...');
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            console.log('Download URL obtained:', downloadURL);

            // Create presentation record in Firestore
            const createResult = await createPresentationRecord(
              presentationData,
              downloadURL,
              storagePath
            );

            console.log('Presentation record created:', createResult);

            resolve({
              success: true,
              presentationId: createResult.presentationId!,
              message: 'Video uploaded and presentation created successfully',
              downloadURL,
              storagePath,
              fileName: videoFile.name,
              fileSize: videoFile.size
            });
          } catch (error) {
            console.error('Error creating presentation record:', error);
            reject(error);
          }
        }
      );
    });

  } catch (error) {
    console.error('Presentation upload error:', error);
    throw error;
  }
};

/**
 * Simple presentation upload for small files (legacy support)
 * @param {Object} presentationData - Presentation metadata
 * @param {File} videoFile - Video file to upload
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<UploadResult>} Upload result
 */
export const uploadPresentationSimple = async (presentationData: any, videoFile: File, onProgress: ((progress: number) => void) | null = null): Promise<UploadResult> => {
  // For small files or fallback, use the chunked upload anyway
  return uploadPresentation(presentationData, videoFile, onProgress);
};

// Export the ChunkedUploader class for advanced usage
export { ChunkedUploader };